import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PKG_INTEREST = [
  ["daily", "Daily Pass"],
  ["weekly", "Weekly Pass"],
  ["monthly", "Monthly Pass"],
  ["camps", "Holiday Camps"],
  ["birthday", "Birthday Parties"],
];

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", package_interest: [] });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [live, setLive] = useState(null);
  useEffect(() => { api.get("/public/settings").then((r) => setLive(!!r.data.registration_live)).catch(() => setLive(true)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleInterest = (k) => setForm((f) => ({ ...f, package_interest: f.package_interest.includes(k) ? f.package_interest.filter((x) => x !== k) : [...f.package_interest, k] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created! Let's add your first explorer.");
      nav("/dashboard/explorers");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard")}`;
  };

  if (live === false) {
    return (
      <AuthShell title="Pre-Registration Opens Soon" sub="Registration opens Friday, September 25.">
        <div className="text-center" data-testid="register-gated">
          <p className="text-slate-600">Pre-registration isn't open just yet. Join the Adventure Rock mailing list and you'll be the first to know the moment it goes live.</p>
          <Link to="/join"><Button className="mt-6 w-full bg-gold hover:bg-gold/90 text-navy rounded-full font-bold" data-testid="register-gated-join">Join the Mailing List</Button></Link>
          <p className="text-center text-sm text-slate-500 mt-6">Already registered? <Link to="/login" className="text-turquoise font-semibold hover:underline">Log in</Link></p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Pre-Register Your Explorer" sub="Create a parent account to begin the adventure.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Your name</Label><Input value={form.name} onChange={set("name")} required data-testid="reg-name" className="rounded-xl" /></div>
        <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="reg-email" className="rounded-xl" /></div>
        <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="reg-phone" className="rounded-xl" /></div>
        <div><Label>Password</Label><Input type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="reg-password" className="rounded-xl" /></div>
        <div>
          <Label>What are you interested in?</Label>
          <p className="text-xs text-slate-400 mb-2">Optional — helps us plan. Pick any that apply.</p>
          <div className="grid grid-cols-3 gap-2" data-testid="reg-interest-grid">
            {PKG_INTEREST.map(([k, l]) => (
              <button type="button" key={k} onClick={() => toggleInterest(k)} data-testid={`reg-interest-${k}`}
                className={`rounded-xl border-2 px-2 py-2.5 text-sm font-semibold transition-colors ${form.package_interest.includes(k) ? "border-turquoise bg-turquoise/10 text-navy" : "border-slate-200 text-slate-500 hover:border-turquoise/50"}`}>{l}</button>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-gold hover:bg-gold/90 text-navy rounded-full font-bold" data-testid="reg-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}</Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">OR</span><div className="h-px bg-slate-200 flex-1" /></div>
      <Button type="button" onClick={google} variant="outline" className="w-full rounded-full border-slate-300 font-semibold" data-testid="google-register-btn">
        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" /> Continue with Google
      </Button>
      <p className="text-center text-sm text-slate-500 mt-6">Already registered? <Link to="/login" className="text-turquoise font-semibold hover:underline">Log in</Link></p>
    </AuthShell>
  );
}
